import React from 'react';
import { Row, Col, Typography, Divider, Tag } from 'antd';
import { formatFullDomainUrl } from '../../../services/utils/domainUtils';
import { greyScaleTokens, variantTokens } from '../../../theme/theme';
import Tutorial from './Tutorial';
// Render SEO keywords as tags
const { Title, Text } = Typography;

const ReviewStep = ({ formData }) => {
    // Lingue supportate per le meta descriptions
    const supportedLanguages = [
        { key: 'it', label: 'Italian', fullName: 'Italian' },
        { key: 'en', label: 'English', fullName: 'English' },
        { key: 'fr', label: 'French', fullName: 'French' },
        { key: 'es', label: 'Spanish', fullName: 'Spanish' },
        { key: 'de', label: 'German', fullName: 'German' }
    ];

    // Funzione per ottenere le meta descriptions disponibili
    const getAvailableMetaDescriptions = () => {
        const descriptions = [];

        // Controlla le descrizioni nella struttura nidificata
        if (formData.seo && formData.seo.metaDescriptions) {
            supportedLanguages.forEach(lang => {
                const description = formData.seo.metaDescriptions[lang.key];
                if (description && description.trim()) {
                    descriptions.push({
                        language: lang.fullName,
                        content: description
                    });
                }
            });
        }

        // Se non abbiamo descrizioni nella struttura nidificata, controlla i campi piatti
        if (descriptions.length === 0) {
            const languageFieldMap = {
                'it': 'descriptionIt',
                'en': 'descriptionEn',
                'fr': 'descriptionFr',
                'es': 'descriptionEs',
                'de': 'descriptionDe'
            };

            supportedLanguages.forEach(lang => {
                const fieldName = languageFieldMap[lang.key];
                const description = formData[fieldName];
                if (description && description.trim()) {
                    descriptions.push({
                        language: lang.fullName,
                        content: description
                    });
                }
            });
        }

        // Fallback per il campo legacy metaDescription
        if (descriptions.length === 0 && formData.metaDescription) {
            descriptions.push({
                language: 'Default',
                content: formData.metaDescription
            });
        }

        return descriptions;
    };

    const availableDescriptions = getAvailableMetaDescriptions();

    return (
        <Row gutter={32} style={{ minHeight: 400 }}>
            {/* Colonna Form */}
            <Col xs={24} md={12}>
                <div style={{ padding: '1.25rem 1rem' }}>
                    <Title level={4}>Review Your Catalogue</Title>
                    <Text type="secondary">
                        Review your catalogue settings before creating it.
                    </Text>

                    <div style={{ marginTop: '1.5rem' }}>
                        <Divider orientation="left">Basic Information</Divider>
                        <p><strong>Catalogue Name:</strong> {formData.catalogueName || '-'}</p>
                        <p><strong>Subdomain:</strong> {formData.domain ? formatFullDomainUrl(formData.domain) : '-'}</p>

                        <Divider orientation="left">SEO</Divider>
                        <p><strong>Title:</strong> {formData.seo?.title || formData.titleSeo || '-'}</p>

                        <p><strong>Meta Descriptions:</strong></p>
                        {availableDescriptions.length > 0 ? (
                            <div style={{ marginLeft: '20px' }}>
                                {availableDescriptions.map((desc, index) => (
                                    <div key={index} style={{ marginBottom: '8px' }}>
                                        <strong>{desc.language}:</strong> {desc.content}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ marginLeft: '20px' }}>-</p>
                        )}

                        <p><strong>Keywords:</strong></p>
                        {Array.isArray(formData.seo?.keywords) && formData.seo.keywords.length ? (
                            <div>{formData.seo.keywords.map((k, index) =>
                                <Tag
                                    key={`keyword-${index}-${k}`}
                                    style={{
                                        margin: '2px',
                                        backgroundColor: greyScaleTokens.grey200,
                                        color: '#000',
                                        border: 'none',
                                        borderRadius: '2px'
                                    }}
                                >{k}</Tag>
                            )}</div>
                        ) : <p>-</p>}
                    </div>

                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                        <Text style={{ color: variantTokens.accent }}>
                            Are you sure you want to create this catalogue? It will be saved as a draft.
                        </Text>
                    </div>
                </div>
            </Col>

            {/* Colonna Tutorial */}
            <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                <Tutorial
                    title="Review Your Catalogue"
                    description={
                        <>
                            Before finalizing your catalogue creation, please review all the information you've entered.<br /><br />
                            Make sure that:
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li>The catalogue name is correct and represents your brand</li>
                                <li>The subdomain is appropriate and easy to remember</li>
                                <li>SEO information is optimized for better visibility</li>
                                <li>Meta descriptions are provided in the languages you need</li>
                            </ul>
                        </>
                    }
                />
            </Col>
        </Row>
    );
};

export default ReviewStep;
